import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/utils";
import { Loader2 } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-center px-[12px] py-[13px] rounded-[12px] justify-center gap-2 whitespace-nowrap transition-all duration-200 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Primary button using your exact button color
        primary: "bg-button text-white hover:bg-button-700",

        // Gradient buttons using your palette
        "primary-gradient":
          "bg-gradient-button text-white hover:bg-gradient-button-hover",
        "music-gradient": "bg-gradient-music text-white hover:opacity-90",
        "sunset-gradient": "bg-gradient-sunset text-white hover:opacity-90",

        // Outlined variants
        "primary-outline":
          "border-[1px] border-[#5C88C5] text-borderBlack bg-white hover:bg-primary",
        "secondary-outline":
          "border-2 border-secondary-400 text-secondary-700 bg-white hover:bg-secondary-50",

        // Ghost variants
        "primary-ghost": "text-button hover:bg-primary",
        "secondary-ghost": "text-secondary-700 hover:bg-secondary-100",

        // Light variants using your palette
        light: "bg-primary text-button hover:bg-primary-200",
        band: "bg-band text-button hover:bg-band-200",

        // Neutral variants
        neutral: "bg-secondary text-secondary-700 hover:bg-secondary-300",

        // Disabled state
        disabled: "bg-secondary-200 text-secondary-500 cursor-default",
      },
      size: {
        sm: "h-8",
        md: "h-[48px]",
        lg: "h-[48px]",
        xl: "h-[48px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        className={`${cn(
          buttonVariants({ variant, size, className })
        )} text-h3-d`}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {!loading && leftIcon && leftIcon}
        {children}
        {!loading && rightIcon && rightIcon}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
